import { Node } from 'reactflow';
import { FlowService } from '../../service/FlowService';
import { nodeSchema } from '../myFunctionsCoreDisplay';
import { handleChanges } from './changeTypes';

type SaveCacheEntry = {
  lastData: any;
  timer: ReturnType<typeof setTimeout> | null;
  timestamp: number;
};

const saveCache = new Map<string, SaveCacheEntry>();
const DEBOUNCE_DELAY = 800;
const CACHE_CLEANUP_INTERVAL = 60000;
const CACHE_MAX_AGE = 300000;

const CRITICAL_CHANGE_TYPES = [
  'execute',
  'delete-edges',
  'update-edge',
  'connect',
  'save-logs',
];

const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

let cleanupIntervalId: ReturnType<typeof setInterval> | null = null;

const startCacheCleanup = () => {
  if (cleanupIntervalId) return;

  cleanupIntervalId = setInterval(() => {
    const now = Date.now();
    const entriesToDelete: string[] = [];

    saveCache.forEach((entry, key) => {
      if (now - entry.timestamp > CACHE_MAX_AGE) {
        if (entry.timer) {
          clearTimeout(entry.timer);
        }
        entriesToDelete.push(key);
      }
    });

    entriesToDelete.forEach(key => saveCache.delete(key));
  }, CACHE_CLEANUP_INTERVAL);
};

startCacheCleanup();

export const nodeDataAutoSaveDynamic = ({
  setEdges,
  setNodeState,
  id,
  flowTriggerData,
  flowId,
  objectData,
  setSchema,
  newEdges,
  changeType = 'default',
  connectData,
  deleteEdges,
  updateEdge,
  objectCallerData,
  setSaving,
}: any) => {
  const edges = [...newEdges];

  setNodeState((node: Node[]) => {
    const validNodes = node.filter(n => n !== undefined && n !== null);

    const nds = handleChanges({
      id,
      objectData,
      changeType,
      connectData,
      deleteEdges,
      updateEdge,
      objectCallerData,
      edges,
      flowTriggerData,
      node: validNodes,
    }).nds;

    const validResult = nds.filter((n: Node) => n !== undefined && n !== null);

    nodeSchema(validResult, setSchema);

    return validResult;
  });

  if (!flowId) return;

  const cacheKey = `${flowId}:${id || 'global'}:${changeType}`;
  const isCriticalChange = CRITICAL_CHANGE_TYPES.includes(changeType);

  const onSaveNodeData = async () => {
    const saving = {
      finished: false,
      lastSaved: new Date().toISOString(),
      status: 'loading',
      flowId: flowId,
    };

    setSaving(saving);

    try {
      await FlowService.updateNodeData(flowId, {
        id,
        data: { ...objectData, isEmptyState: false },
        changeType,
      });

      const saving = {
        finished: true,
        lastSaved: new Date().toISOString(),
        status: 'saved',
        flowId: flowId,
      };

      setSaving(saving);

      const cacheEntry = saveCache.get(cacheKey);
      if (cacheEntry) {
        cacheEntry.lastData = objectData;
        cacheEntry.timestamp = Date.now();
      } else {
        saveCache.set(cacheKey, {
          lastData: objectData,
          timer: null,
          timestamp: Date.now(),
        });
      }
    } catch (error) {
      const saving = {
        finished: false,
        lastSaved: new Date().toISOString(),
        status: 'error',
        flowId: flowId,
      };

      setSaving(saving);
    }
  };

  if (isCriticalChange) {
    onSaveNodeData();
    return;
  }

  const cachedEntry = saveCache.get(cacheKey);

  if (cachedEntry?.lastData && deepEqual(cachedEntry.lastData, objectData)) {
    return;
  }

  if (cachedEntry?.timer) {
    clearTimeout(cachedEntry.timer);
  }

  const timer = setTimeout(() => {
    onSaveNodeData();
  }, DEBOUNCE_DELAY);

  if (cachedEntry) {
    cachedEntry.timer = timer;
    cachedEntry.timestamp = Date.now();
  } else {
    saveCache.set(cacheKey, {
      lastData: null,
      timer: timer,
      timestamp: Date.now(),
    });
  }
};

export const nodePassOnlyText = {
  imageReaderNode: 'inputPreview',
  audioReaderNode: 'inputPreview',
  documentSummarizer: 'inputPreview',
  arrayNode: 'content',
  imageGenerator: 'inputPreview',
  mathFunction: 'varId',
  fluxBox: 'varId',
  libraryNode: 'varId',
  fileSave: 'varId',
};

export const nodePassOnlyTextRealHandleId = {
  imageReaderNode: 'inputPreview',
  audioReaderNode: 'inputPreview',
  documentSummarizer: 'inputPreview',
  arrayNode: 'content',
  imageGenerator: 'inputPreview',
  mathFunction: 'varId',
  fluxBox: 'varId',
  libraryNode: 'varId',
  fileSave: 'varId',
};
